import React, { forwardRef, useMemo } from "react";

import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { toast } from "sonner";

import AdvertisingVideoUpload from "./AdvertisingVideoUpload";
import OfferAPI from "../../api/OfferAPI";
import { FormProviderWithTranslations } from "../../contexts/FormTranslationsContext";
import {
  apiToFormTranslations,
  apiToVideoByLanguageUuid,
  createTranslationResponseToFormField,
  formToApiTranslations,
  getAdvertisingTranslationsFormSchema,
} from "../../utils/translationAdapters/advertising";
import Input from "../form/Input";
import FormTranslations from "../formTranslations/FormTranslations";
import FormTranslationsHeader from "../formTranslations/FormTranslationsHeader";
import Spinner from "../utils/Spinner";

import type { Language } from "../../api/ProjectAPI.types";
import type {
  FormTranslationsHandle,
  PageFieldArray,
} from "../../contexts/FormTranslationsContext";
import type { AppState } from "../../reducers";
import type { IAdvertisingSingle } from "../../types";
import type { AdvertisingTranslationFormField } from "../../utils/translationAdapters/advertising";

export type AdvertisingTranslationsHandle = FormTranslationsHandle<
  ReturnType<typeof formToApiTranslations>
>;

interface AdvertisingTranslationsProps {
  campaign: IAdvertisingSingle;
}

interface AdvertisingTranslationsFormProps extends AdvertisingTranslationsProps {
  languageList: Language[];
}

const AdvertisingTranslationsForm = forwardRef<
  AdvertisingTranslationsHandle,
  AdvertisingTranslationsFormProps
>(({ campaign, languageList }, ref) => {
  const { t, i18n } = useTranslation("editor-advertisings");
  const { projectSlug } = useParams<{ projectSlug: string }>();

  const queryClient = useQueryClient();

  const resolver = useMemo(
    () => zodResolver(getAdvertisingTranslationsFormSchema()),
    // i18n.language is not read in the factory but the message it builds is:
    // without this dep the validation message would keep the previous language.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [i18n.language],
  );

  const getSingleAdvertisingQuery = OfferAPI.generateQuery({
    fn: OfferAPI.getSingleAdvertising,
    params: [{ uuid: campaign.uuid, projectSlug }],
  });

  const { data: campaignData } = useQuery(getSingleAdvertisingQuery);

  const videoByLanguageUuid = useMemo(
    () => apiToVideoByLanguageUuid(campaignData?.translations ?? []),
    [campaignData],
  );

  const methods = useForm<
    PageFieldArray<AdvertisingTranslationFormField>,
    unknown,
    PageFieldArray<AdvertisingTranslationFormField>
  >({
    mode: "onChange",
    resolver,
    defaultValues: {
      translations: apiToFormTranslations(campaign.translations, languageList),
    },
  });

  const addLanguageMutation = useMutation({
    mutationFn: async ({ languageUuid }: { languageUuid: string }) =>
      OfferAPI.createAdvertisingTranslation({
        uuid: campaign.uuid,
        languageUuid,
        projectSlug,
      }),
    onSuccess: (response) => {
      queryClient.setQueryData<IAdvertisingSingle>(
        getSingleAdvertisingQuery.queryKey,
        (previous) =>
          previous && {
            ...previous,
            translations: [
              ...previous.translations,
              {
                ...response,
                filename: response.filename ?? "",
                video_url: response.video_url ?? "",
              },
            ],
          },
      );
    },
    onError: (error) => {
      console.error("Error creating advertising translation:", error);
      toast.error(t("genericError", { ns: "error" }));
    },
  });

  const deleteLanguageMutation = useMutation({
    mutationFn: async (
      removedTranslation: AdvertisingTranslationFormField,
    ): Promise<void> => {
      if (!removedTranslation.uuid) {
        throw new Error(
          "Could not determine which advertising translation to delete",
        );
      }

      await OfferAPI.deleteAdvertisingTranslation({
        uuid: campaign.uuid,
        translationUuid: removedTranslation.uuid,
        projectSlug,
      });
    },
    onSuccess: () => {
      toast.success(t("form:deleteLanguageSuccess"));
    },
    onError: (error) => {
      console.error("Error deleting advertising translation:", error);
      toast.error(t("genericError", { ns: "error" }));
    },
  });

  const deleteVideoMutation = useMutation({
    mutationFn: async (translationIndex: number): Promise<void> => {
      const translationUuid =
        methods.getValues("translations")[translationIndex]?.uuid;

      if (!translationUuid) {
        throw new Error(
          "Could not determine which advertising translation's video to delete",
        );
      }

      await OfferAPI.deleteAdvertisingFile({
        uuid: campaign.uuid,
        translationUuid,
        projectSlug,
      });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries(getSingleAdvertisingQuery.queryKey);
      toast.success(t("videoUpload.deleteSuccess"));
    },
    onError: (error) => {
      console.error("Error deleting advertising video:", error);
      toast.error(t("genericError", { ns: "error" }));
    },
  });

  const getSetAsDefaultDisabledReason = (
    translation: AdvertisingTranslationFormField,
  ): string | undefined =>
    videoByLanguageUuid[translation.language_uuid ?? ""]?.video_url
      ? undefined
      : t("videoUpload.setAsDefaultDisabledReason");

  return (
    <div className="bg-card flex flex-col rounded-[0.3125rem] p-[1.5625rem] drop-shadow-md">
      <FormProviderWithTranslations
        getSetAsDefaultDisabledReason={getSetAsDefaultDisabledReason}
        handleRef={ref}
        methods={methods}
        onAddLanguage={addLanguageMutation.mutateAsync}
        toFormField={createTranslationResponseToFormField}
        formToApiTranslations={(
          formTranslations: AdvertisingTranslationFormField[],
        ) => formToApiTranslations(formTranslations, videoByLanguageUuid)}
        onDeleteLanguage={async (
          _updatedFormTranslations,
          removedTranslation,
        ) => deleteLanguageMutation.mutateAsync(removedTranslation)}
      >
        <FormTranslationsHeader />
        <div className="flex flex-col gap-8">
          <AdvertisingVideoUpload
            campaignUuid={campaign.uuid}
            isDeletingVideo={deleteVideoMutation.isLoading}
            onDeleteVideo={deleteVideoMutation.mutateAsync}
            videoByLanguageUuid={videoByLanguageUuid}
          />
          <FormTranslations>
            <Input label={t("url")} name="url" type="text" />
          </FormTranslations>
        </div>
      </FormProviderWithTranslations>
    </div>
  );
});

AdvertisingTranslationsForm.displayName = "AdvertisingTranslationsForm";

const AdvertisingTranslations = forwardRef<
  AdvertisingTranslationsHandle,
  AdvertisingTranslationsProps
>(({ campaign }, ref) => {
  const languageList = useSelector(
    (state: AppState) => state.fetchReducer.languages.data,
  );

  if (!languageList.length) {
    return <Spinner />;
  }

  return (
    <AdvertisingTranslationsForm
      ref={ref}
      campaign={campaign}
      languageList={languageList}
    />
  );
});

AdvertisingTranslations.displayName = "AdvertisingTranslations";

export default AdvertisingTranslations;
